import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Pencil, Plus, Repeat, Search, Trash2 } from "lucide-react";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { Avatar, Badge, Button, Card, EmptyState, Modal, PageHeader, Skeleton } from "../components/ui";
import { SHIFT_LABEL, SHIFT_TONE, formatMinutes, isAccountAdmin, isManagerOrAbove } from "../lib/people";
import type { RosterAssignment, Shift, ShiftTemplate, ShiftTemplateWrite } from "../api/types";

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/**
 * The roster uses the same colour for a template everywhere: legend, grid
 * cells, template manager, assignment list and both edit modals. The API has no
 * colour field, so the colour is derived from the template id. That keeps it
 * stable across refreshes without adding a migration.
 */
export type ShiftTone = {
  key: string;
  dot: string;
  chip: string;
  bg: string;
  text: string;
  hover: string;
  ring: string;
};

const SHIFT_TONES: ShiftTone[] = [
  {
    key: "sky",
    dot: "bg-sky-500",
    chip: "bg-sky-50 text-sky-800 ring-sky-200",
    bg: "bg-sky-50",
    text: "text-sky-900",
    hover: "hover:bg-sky-100",
    ring: "ring-sky-200",
  },
  {
    key: "violet",
    dot: "bg-violet-500",
    chip: "bg-violet-50 text-violet-800 ring-violet-200",
    bg: "bg-violet-50",
    text: "text-violet-900",
    hover: "hover:bg-violet-100",
    ring: "ring-violet-200",
  },
  {
    key: "emerald",
    dot: "bg-emerald-500",
    chip: "bg-emerald-50 text-emerald-800 ring-emerald-200",
    bg: "bg-emerald-50",
    text: "text-emerald-900",
    hover: "hover:bg-emerald-100",
    ring: "ring-emerald-200",
  },
  {
    key: "amber",
    dot: "bg-amber-500",
    chip: "bg-amber-50 text-amber-800 ring-amber-200",
    bg: "bg-amber-50",
    text: "text-amber-900",
    hover: "hover:bg-amber-100",
    ring: "ring-amber-200",
  },
  {
    key: "rose",
    dot: "bg-rose-500",
    chip: "bg-rose-50 text-rose-800 ring-rose-200",
    bg: "bg-rose-50",
    text: "text-rose-900",
    hover: "hover:bg-rose-100",
    ring: "ring-rose-200",
  },
  {
    key: "cyan",
    dot: "bg-cyan-500",
    chip: "bg-cyan-50 text-cyan-800 ring-cyan-200",
    bg: "bg-cyan-50",
    text: "text-cyan-900",
    hover: "hover:bg-cyan-100",
    ring: "ring-cyan-200",
  },
  {
    key: "indigo",
    dot: "bg-indigo-500",
    chip: "bg-indigo-50 text-indigo-800 ring-indigo-200",
    bg: "bg-indigo-50",
    text: "text-indigo-900",
    hover: "hover:bg-indigo-100",
    ring: "ring-indigo-200",
  },
  {
    key: "orange",
    dot: "bg-orange-500",
    chip: "bg-orange-50 text-orange-800 ring-orange-200",
    bg: "bg-orange-50",
    text: "text-orange-900",
    hover: "hover:bg-orange-100",
    ring: "ring-orange-200",
  },
];

const NEUTRAL_TONE: ShiftTone = {
  key: "neutral",
  dot: "bg-slate-400",
  chip: "bg-slate-50 text-slate-700 ring-slate-200",
  bg: "bg-slate-50",
  text: "text-slate-700",
  hover: "hover:bg-slate-100",
  ring: "ring-slate-200",
};

const LEAVE_TONE: ShiftTone = {
  key: "fuchsia",
  dot: "bg-fuchsia-500",
  chip: "bg-fuchsia-50 text-fuchsia-800 ring-fuchsia-200",
  bg: "bg-fuchsia-50",
  text: "text-fuchsia-900",
  hover: "hover:bg-fuchsia-100",
  ring: "ring-fuchsia-200",
};

const OFF_TONE: ShiftTone = {
  key: "off",
  dot: "bg-slate-300",
  chip: "bg-slate-100 text-slate-500 ring-slate-200",
  bg: "bg-slate-50",
  text: "text-slate-400",
  hover: "hover:bg-slate-100",
  ring: "ring-slate-200",
};

export function shiftToneFor(key: string | null | undefined): ShiftTone {
  if (!key) return NEUTRAL_TONE;
  let hash = 0;
  for (let index = 0; index < key.length; index += 1) {
    hash = (hash * 31 + key.charCodeAt(index)) >>> 0;
  }
  return SHIFT_TONES[hash % SHIFT_TONES.length] ?? NEUTRAL_TONE;
}

function templateTone(template?: ShiftTemplate | null): ShiftTone {
  return shiftToneFor(template?.id ?? template?.name);
}

function templateChip(template: ShiftTemplate): string {
  return `inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${templateTone(template).chip}`;
}

function initialsFor(name?: string | null): string {
  const value = name?.trim();
  if (!value) return "S";
  return value
    .split(/\s+/)
    .map((part) => part[0] ?? "")
    .join("")
    .slice(0, 3)
    .toUpperCase();
}

function shiftCodeFor(shift: Shift, template?: ShiftTemplate | null): string {
  const configured = template?.code?.trim();
  if (configured) return configured.toUpperCase();
  return initialsFor(template?.name ?? shift.template_name);
}


/** The employment fields the roster board reads, independent of response shape. */
type RosterEmployee = {
  id: string;
  display_name: string;
  employee_code?: string;
  designation?: string;
  branch?: string | null;
  department?: string | null;
  status?: string;
  photo?: string;
};

/**
 * Roster: expected work, as a weekly board.
 *
 * Rows are employees, columns are the seven days. A cell shows the assigned
 * shift (or OFF, or an approved leave overlay). Managers edit a cell in place;
 * staff see only their own published row. Actual work lives in Attendance --
 * this page never records it.
 */
export function RosterPage() {
  const { user } = useAuth();
  const canManage = isManagerOrAbove(user?.role);
  const canManageDefaultShift = isAccountAdmin(user?.role);
  const queryClient = useQueryClient();

  const [weekStart, setWeekStart] = useState(() => startOfWeek(isoDate(new Date())));
  const [viewMode, setViewMode] = useState<"week" | "month">("week");
  const [branchFilter, setBranchFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [employeeFilter, setEmployeeFilter] = useState("");
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [editing, setEditing] = useState<{ employeeId: string; date: string } | null>(null);
  const [tab, setTab] = useState<"assignments" | "templates" | "default">("assignments");
  const [publishError, setPublishError] = useState<string | null>(null);
  const [publishNotice, setPublishNotice] = useState<string | null>(null);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const publishingRef = useRef(false);

  const from = viewMode === "week" ? weekStart : startOfMonth(weekStart);
  const to = viewMode === "week" ? addDays(weekStart, 6) : endOfMonth(weekStart);
  const periodLabel = viewMode === "week" ? "week" : "month";

  const shifts = useQuery({
    queryKey: ["roster", from, to, branchFilter, departmentFilter, employeeFilter],
    queryFn: ({ signal }) =>
      api.shiftsPage(
        {
          from,
          to,
          employee: employeeFilter || undefined,
          branch: branchFilter || undefined,
          department: departmentFilter || undefined,
          page_size: 500,
        },
        signal,
      ),
    placeholderData: (prev) => prev,
  });

  const employees = useQuery({
    // A dedicated key: `employee-options` is a *plain array* from the directory
    // endpoint on several other pages. Sharing that key made this page read an
    // array where it expected a paginated `{ results }`, so the grid silently
    // rendered "No employees to roster" depending on which page loaded first.
    queryKey: ["roster-employees"],
    // Full records, not the directory: the branch and department filters need
    // the ids the API scopes on, and the directory only carries display names.
    queryFn: ({ signal }) => api.employeeList({ page_size: 200, ordering: "last_name" }, signal),
    enabled: canManage,
    staleTime: 5 * 60_000,
  });

  const templates = useQuery({
    queryKey: ["shift-templates"],
    queryFn: ({ signal }) => api.shiftTemplatesPage({ page_size: 100 }, signal),
    enabled: canManage,
  });

  const branches = useQuery({
    queryKey: ["branches"],
    queryFn: ({ signal }) => api.branches(signal),
    enabled: canManage,
  });
  const departments = useQuery({
    queryKey: ["departments", 100],
    queryFn: ({ signal }) => api.departmentsPage({ page_size: 100 }, signal),
    enabled: canManage,
  });

  const leave = useQuery({
    queryKey: ["roster-leave", from, to],
    queryFn: ({ signal }) =>
      api.leaveRequests(
        { status: "approved", from, to, page_size: 500 },
        signal,
      ),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ["roster"] });

  const draftShifts = (shifts.data?.results ?? []).filter((shift) => shift.status === "draft");

  const publishWeek = useMutation({
    mutationFn: (drafts: Shift[]) => publishDraftShifts(drafts, api.publishShift),
    onSuccess: () => {
      publishingRef.current = false;
      setConfirmPublish(false);
      setPublishError(null);
      setPublishNotice("Roster published successfully.");
      invalidate();
    },
    onError: (caught) => {
      publishingRef.current = false;
      setPublishNotice(null);
      setPublishError(errorMessage(caught));
      // A partial failure has already published some shifts; refresh so the
      // remaining drafts are the only thing a retry can send.
      invalidate();
    },
  });

  function openPublishConfirmation() {
    if (publishingRef.current || publishWeek.isPending || draftShifts.length === 0) return;
    setPublishError(null);
    setPublishNotice(null);
    setConfirmPublish(true);
  }

  function closePublishConfirmation() {
    if (publishingRef.current || publishWeek.isPending) return;
    setConfirmPublish(false);
    setPublishError(null);
  }

  const days = useMemo(
    () =>
      viewMode === "month"
        ? monthDays(weekStart)
        : Array.from({ length: daysBetween(from, to) + 1 }, (_, i) =>
            addDays(from, i),
          ),
    [viewMode, weekStart, from, to],
  );

  // The roster employees endpoint is paginated, but tolerate a plain array too:
  // a stale/shared cache or a future shape change must never turn the whole
  // board into "No employees to roster".
  const employeePayload = employees.data as unknown as
    | RosterEmployee[]
    | { results?: RosterEmployee[] }
    | undefined;
  const allRows: RosterEmployee[] = Array.isArray(employeePayload)
    ? employeePayload
    : employeePayload?.results ?? [];
  const normalizedSearch = employeeSearch.trim().toLowerCase();
  const visibleRows = allRows.filter((person) => {
    if (statusFilter ? person.status !== statusFilter : person.status === "exited") {
      return false;
    }
    if (branchFilter && person.branch !== branchFilter) return false;
    if (departmentFilter && person.department !== departmentFilter) return false;
    if (employeeFilter && person.id !== employeeFilter) return false;
    if (normalizedSearch) {
      return (
        person.display_name.toLowerCase().includes(normalizedSearch) ||
        (person.employee_code ?? "").toLowerCase().includes(normalizedSearch)
      );
    }
    return true;
  });

  const shiftMap = useMemo(() => {
    const map = new Map<string, Shift>();
    for (const shift of shifts.data?.results ?? []) {
      map.set(`${shift.employee}:${shift.shift_date}`, shift);
    }
    return map;
  }, [shifts.data]);

  const templateMap = useMemo(
    () =>
      new Map(
        (templates.data?.results ?? []).map((template) => [template.id, template]),
      ),
    [templates.data],
  );

  const leaveMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const request of leave.data?.results ?? []) {
      // The API is asked for approved leave, and this guard keeps a mixed
      // response from ever turning a pending/rejected/cancelled request into
      // an approved overlay.
      if (request.status !== "approved") continue;
      let day = request.from_date;
      while (day <= request.to_date) {
        map.set(`${request.employee}:${day}`, request.leave_type_name ?? "Leave");
        day = addDays(day, 1);
      }
    }
    return map;
  }, [leave.data]);

  // Coverage is counted from the roster rows actually on screen: who is on a
  // shift, off, on leave, or has nothing at all. Deliberately counts, not a
  // percentage -- we have no required-staffing figure, and a made-up "80%
  // covered" would be a number nobody could act on.
  const dayStats = useMemo(
    () =>
      days.map((day) => {
        let onShift = 0;
        let off = 0;
        let leave = 0;
        let notRostered = 0;
        for (const person of visibleRows) {
          const shift = shiftMap.get(`${person.id}:${day}`);
          const onLeave = leaveMap.get(`${person.id}:${day}`);
          if (onLeave) leave += 1;
          else if (shift?.is_off) off += 1;
          else if (shift) onShift += 1;
          else notRostered += 1;
        }
        return { day, onShift, off, leave, notRostered };
      }),
    [days, visibleRows, shiftMap, leaveMap],
  );
  const uncoveredTotal = dayStats.reduce((sum, stat) => sum + stat.notRostered, 0);

  const error = shifts.error;

  // An employee has no employee directory and no edit rights: they see their
  // own published rota as a simple list, not the manager's assignment board.
  if (!canManage) {
    const myShifts = [...(shifts.data?.results ?? [])].sort((a, b) =>
      a.shift_date < b.shift_date ? -1 : 1,
    );
    const myLeave = (leave.data?.results ?? []).filter(
      (request) => request.status === "approved",
    );
    return (
      <>
        <PageHeader
          title="Roster"
          subtitle="Your published shifts."
          actions={
            <>
              <Button onClick={() => setWeekStart(addDays(weekStart, -7))}>
                <ChevronLeft size={15} /> Prev
              </Button>
              <Button onClick={() => setWeekStart(startOfWeek(isoDate(new Date())))}>
                This week
              </Button>
              <Button onClick={() => setWeekStart(addDays(weekStart, 7))}>
                Next <ChevronRight size={15} />
              </Button>
            </>
          }
        />
        <p className="mb-3 text-sm text-slate-600">
          {formatDay(from)} – {formatDay(to)}
        </p>
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          {shifts.isPending ? (
            <Skeleton className="h-40 w-full" />
          ) : shifts.isError ? (
            <EmptyState title="Could not load your roster" body="Please try again." />
          ) : myShifts.length === 0 ? (
            <EmptyState
              title="No published shifts this week"
              body="Your rota appears here once your manager publishes it."
            />
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2.5">Date</th>
                  <th className="px-4 py-2.5">Shift</th>
                  <th className="px-4 py-2.5">Hours</th>
                  <th className="px-4 py-2.5">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {myShifts.map((shift) => (
                  <tr key={shift.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2.5 text-sm text-slate-700">{formatDay(shift.shift_date)}</td>
                    <td className="px-4 py-2.5 text-sm tabular-nums text-slate-600">
                      {shift.start_time.slice(0, 5)}–{shift.end_time.slice(0, 5)}
                      {shift.spans_midnight ? (
                        <span className="ml-2 text-xs text-sky-600">overnight</span>
                      ) : null}
                    </td>
                    <td className="px-4 py-2.5 text-sm tabular-nums text-slate-700">
                      {formatMinutes(shift.duration_minutes)}
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge tone={SHIFT_TONE[shift.status ?? "draft"]}>
                        {SHIFT_LABEL[shift.status ?? "draft"]}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {myLeave.length ? (
          <Card className="mt-4">
            <h2 className="mb-2 text-sm font-semibold text-slate-900">
              Approved leave
            </h2>
            <ul className="space-y-1.5">
              {myLeave.map((request) => (
                <li
                  key={request.id}
                  className="flex flex-wrap items-center justify-between gap-2 text-sm"
                >
                  <span className="text-slate-700">
                    {formatDay(request.from_date)} - {formatDay(request.to_date)}
                  </span>
                  <span className="text-sky-600">
                    {`${request.leave_type_name || "Leave"} (${request.days} day${
                      Number(request.days) === 1 ? "" : "s"
                    })`}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        ) : null}
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Roster"
        subtitle={
          canManage
            ? "Expected work. Click a cell to assign a shift, mark OFF, or clear it."
            : "Your published shifts."
        }
        actions={
          canManage ? (
            <>
              <Button
                variant="primary"
                disabled={draftShifts.length === 0 || publishWeek.isPending}
                onClick={openPublishConfirmation}
                title={`Publish every draft shift this ${periodLabel} so staff can see their rota`}
              >
                Publish {periodLabel}
                {draftShifts.length ? ` (${draftShifts.length})` : ""}
              </Button>
              <Link to="/people/attendance">
                <Button variant="secondary">Attendance →</Button>
              </Link>
            </>
          ) : null
        }
      />

      <div
        role="tablist"
        aria-label="Roster sections"
        className="mb-3 flex flex-wrap gap-1 border-b border-slate-200"
      >
        {(
          [
            ["assignments", "Assignments"],
            ["templates", "Shift Templates"],
            ["default", "Default Shift"],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={tab === value}
            onClick={() => setTab(value)}
            className={
              tab === value
                ? "-mb-px border-b-2 border-slate-900 px-2.5 py-1.5 text-xs font-semibold text-slate-900"
                : "-mb-px border-b-2 border-transparent px-2.5 py-1.5 text-xs text-slate-500 hover:text-slate-800"
            }
          >
            {label}
          </button>
        ))}
      </div>

      {publishNotice ? (
        <p
          role="status"
          className="mb-4 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
        >
          {publishNotice}
        </p>
      ) : null}

      {publishError ? (
        <p role="alert" className="mb-4 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {publishError}
        </p>
      ) : null}

      {tab === "assignments" ? (
        <AssignmentsPanel
          employees={allRows}
          templates={templates.data?.results ?? []}
          branches={branches.data ?? []}
          departments={departments.data?.results ?? []}
          defaultFrom={from}
          defaultTo={to}
          onChanged={() => {
            invalidate();
            void queryClient.invalidateQueries({ queryKey: ["roster-assignments"] });
          }}
        />
      ) : null}

      {tab === "templates" ? (
        <TemplatesPanel
          templates={templates.data?.results ?? []}
          employees={allRows}
          onChanged={() => void queryClient.invalidateQueries({ queryKey: ["shift-templates"] })}
        />
      ) : null}

      {tab === "default" ? (
        <DefaultShiftPanel
          templates={templates.data?.results ?? []}
          canSetDefault={canManageDefaultShift}
          onChanged={() => void queryClient.invalidateQueries({ queryKey: ["shift-templates"] })}
        />
      ) : null}

      {tab === "assignments" ? (
        <>
      <div
        data-section="roster-controls"
        className="mb-3 flex flex-wrap items-center gap-1.5 rounded-lg border border-slate-200 bg-white p-2"
      >
        {canManage ? (
          <>
            <Button
              size="sm"
              onClick={() =>
                setWeekStart(
                  viewMode === "week"
                    ? addDays(weekStart, -7)
                    : addMonths(weekStart, -1),
                )
              }
            >
              <ChevronLeft size={14} /> Prev
            </Button>
            <Button
              size="sm"
              onClick={() =>
                setWeekStart(
                  viewMode === "week"
                    ? startOfWeek(isoDate(new Date()))
                    : startOfMonth(isoDate(new Date())),
                )
              }
            >
              {viewMode === "week" ? "This week" : "This month"}
            </Button>
            <Button
              size="sm"
              onClick={() =>
                setWeekStart(
                  viewMode === "week"
                    ? addDays(weekStart, 7)
                    : addMonths(weekStart, 1),
                )
              }
            >
              Next <ChevronRight size={14} />
            </Button>
            <div className="flex overflow-hidden rounded-md ring-1 ring-inset ring-slate-300">
              <button
                type="button"
                onClick={() => setViewMode("week")}
                className={`px-2 py-1 text-xs ${
                  viewMode === "week"
                    ? "bg-slate-900 text-white"
                    : "bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                Week
              </button>
              <button
                type="button"
                onClick={() => setViewMode("month")}
                className={`px-2 py-1 text-xs ${
                  viewMode === "month"
                    ? "bg-slate-900 text-white"
                    : "bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                Month
              </button>
            </div>
            <label className="relative block">
              <Search
                size={14}
                className="pointer-events-none absolute left-2 top-2.5 text-slate-400"
              />
              <input
                value={employeeSearch}
                onChange={(event) => setEmployeeSearch(event.target.value)}
                placeholder="Search employees"
                className={`${selectClass} w-44 pl-7`}
              />
            </label>
            <select value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)} className={selectClass}>
              <option value="">All branches</option>
              {(branches.data ?? []).map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>
            <select
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
              className={selectClass}
            >
              <option value="">All departments</option>
              {(departments.data?.results ?? []).map((department) => (
                <option key={department.id} value={department.id}>
                  {department.name}
                </option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={selectClass}
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="on_leave">On leave</option>
              <option value="notice_period">Notice period</option>
              <option value="exited">Exited</option>
            </select>
            <select value={employeeFilter} onChange={(e) => setEmployeeFilter(e.target.value)} className={selectClass}>
              <option value="">All employees</option>
              {allRows.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.display_name}
                </option>
              ))}
            </select>
          </>
        ) : null}
        <span className="ml-auto text-xs text-slate-600">
          {viewMode === "month"
            ? formatMonthYear(weekStart)
            : `${formatDay(from)} – ${formatDay(to)}`}
          {canManage && uncoveredTotal > 0 ? (
            <span className="ml-2 font-medium text-amber-600">
              · {uncoveredTotal} unrostered
            </span>
          ) : null}
          {canManage ? (
            <span className="ml-2 inline-flex align-middle">
              {draftShifts.length > 0 ? (
                <Badge tone="amber">{draftShifts.length} draft</Badge>
              ) : (shifts.data?.results.length ?? 0) > 0 ? (
                <Badge tone="emerald">Published</Badge>
              ) : null}
            </span>
          ) : null}
        </span>
      </div>

      {canManage && tab === "assignments" && (templates.data?.results.length ?? 0) > 0 ? (
        <ShiftLegend templates={templates.data?.results ?? []} />
      ) : null}

      {tab === "assignments" ? (
        <div
          data-section="roster-grid"
          className="overflow-x-auto rounded-lg border border-slate-200 bg-white"
        >
        {shifts.isPending || (canManage && employees.isPending) ? (
          <Skeleton className="h-64 w-full" />
        ) : error ? (
          <EmptyState
            title="Could not load the roster"
            body={error instanceof ApiError ? error.message : "Please try again."}
          />
        ) : visibleRows.length === 0 ? (
          <EmptyState
            title="No employees to roster"
            body="Add employees, or clear the branch and department filters."
          />
        ) : (
          <table
            className={`w-full border-collapse ${
              viewMode === "month" ? "min-w-[1120px]" : "min-w-[760px]"
            }`}
          >
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/80">
                <th className="sticky left-0 z-10 w-16 min-w-16 bg-slate-50 px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  Employee ID
                </th>
                <th className="sticky left-16 z-10 min-w-40 bg-slate-50 px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  Employee Name
                </th>
                {days.map((day, index) => (
                  <th
                    key={day}
                    data-day={day}
                    className="w-10 min-w-10 px-1 py-1.5 text-center text-[10px] font-semibold text-slate-500"
                  >
                    <span className="block">
                      {viewMode === "week" ? DAY_NAMES[index] : weekdayShort(day)}
                    </span>
                    <span className="block font-normal text-slate-400">
                      {viewMode === "week" ? formatDay(day) : dayNumber(day)}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visibleRows.map((person) => (
                <tr key={person.id} className="group">
                  <td className="sticky left-0 z-10 w-16 min-w-16 bg-white px-2 py-1 text-[11px] font-medium tabular-nums text-slate-600 group-hover:bg-slate-50">
                    {person.employee_code || "—"}
                  </td>
                  <td className="sticky left-16 z-10 min-w-40 bg-white px-2 py-1 group-hover:bg-slate-50">
                    <Link
                      to={`/people/employees/${person.id}`}
                      title={person.designation ? `${person.display_name} · ${person.designation}` : person.display_name}
                      className="flex items-center gap-1.5"
                    >
                      <Avatar name={person.display_name} src={person.photo} size={22} />
                      <span className="truncate text-xs font-medium text-slate-900">
                        {person.display_name}
                      </span>
                    </Link>
                  </td>
                  {days.map((day) => {
                    const shift = shiftMap.get(`${person.id}:${day}`);
                    const onLeave = leaveMap.get(`${person.id}:${day}`);
                    const template = shift?.template
                      ? templateMap.get(shift.template)
                      : undefined;
                    const code = shift ? shiftCodeFor(shift, template) : undefined;
                    const timeRange = shift
                      ? `${shift.start_time.slice(0, 5)}–${shift.end_time.slice(0, 5)}`
                      : "";
                    const cellTone = onLeave
                      ? LEAVE_TONE
                      : shift?.is_off
                        ? OFF_TONE
                        : shiftToneFor(shift?.template ?? shift?.template_name);
                    const cellTitle = onLeave
                      ? `Approved leave: ${onLeave}`
                      : shift?.has_conflict
                        ? `Conflict · ${template?.name ?? shift.template_name} ${timeRange}`
                        : shift?.is_off
                          ? "Weekly Off"
                          : shift
                            ? `${template?.name ?? shift.template_name} ${timeRange} · ${shift.status === "draft" ? "Draft" : "Published"}`
                            : "Not assigned";
                    return (
                      <td key={day} className="p-0.5 align-middle">
                        <button
                          disabled={!canManage}
                          data-template-id={shift?.template ?? undefined}
                          data-shift-tone={cellTone.key}
                          data-shift-status={shift?.status ?? undefined}
                          data-leave={onLeave ?? undefined}
                          title={cellTitle}
                          onClick={() => canManage && setEditing({ employeeId: person.id, date: day })}
                          className={cellClass(
                            Boolean(shift?.is_off),
                            Boolean(onLeave),
                            canManage,
                            Boolean(shift?.has_conflict),
                            shift?.template ?? shift?.template_name,
                          )}
                        >
                          <CellLabel shift={shift} leaveName={onLeave} code={code} />
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-slate-200 bg-slate-50 text-[10px] text-slate-500">
                <td className="sticky left-0 z-10 w-16 min-w-16 bg-slate-50 px-2 py-1" />
                <td className="sticky left-16 z-10 min-w-40 bg-slate-50 px-2 py-1 font-semibold text-slate-600">
                  Coverage
                </td>
                {dayStats.map((stat) => (
                  <td key={stat.day} className="px-1 py-1 text-center align-middle">
                    <span className="block font-semibold text-slate-700" title="On shift">
                      {stat.onShift}
                    </span>
                    <span
                      className={`block ${stat.notRostered > 0 ? "text-amber-600" : "text-slate-400"}`}
                      title="No shift, leave or off assigned"
                    >
                      {stat.notRostered > 0 ? `${stat.notRostered} open` : "—"}
                    </span>
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        )}
        </div>
      ) : null}
        </>
      ) : null}

      {confirmPublish ? (
        <Modal title="Confirm roster publication" onClose={closePublishConfirmation}>
          <div className="space-y-4">
            <p className="text-sm text-slate-700">
              You are about to publish the roster for{" "}
              <span className="font-medium text-slate-900">
                {formatWeekRange(from, to)}
              </span>
              . Employees will be able to view their assigned shifts after
              publishing.
            </p>
            <div className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">
              <p>
                {draftShifts.length} draft shift
                {draftShifts.length === 1 ? "" : "s"} will be published.
              </p>
              {draftShifts.some((shift) => shift.has_conflict) ? (
                <p className="mt-1 text-amber-700">
                  {draftShifts.filter((shift) => shift.has_conflict).length} draft
                  shift
                  {draftShifts.filter((shift) => shift.has_conflict).length === 1
                    ? " is"
                    : "s are"}{" "}
                  flagged with a scheduling conflict. Review the highlighted cells
                  before publishing.
                </p>
              ) : null}
            </div>
            {publishError ? (
              <p
                role="alert"
                className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700"
              >
                {publishError}
              </p>
            ) : null}
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                onClick={closePublishConfirmation}
                disabled={publishWeek.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  if (publishingRef.current || publishWeek.isPending) return;
                  publishingRef.current = true;
                  publishWeek.mutate(draftShifts);
                }}
                disabled={publishWeek.isPending || draftShifts.length === 0}
              >
                {publishWeek.isPending ? "Publishing…" : "Confirm & Publish"}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}

      {editing ? (
        <ShiftEditModal
          employee={allRows.find((p) => p.id === editing.employeeId)}
          date={editing.date}
          templates={templates.data?.results ?? []}
          currentShift={shiftMap.get(`${editing.employeeId}:${editing.date}`)}
          onClose={() => setEditing(null)}
          onSaved={invalidate}
        />
      ) : null}
    </>
  );
}

function CellLabel({
  shift,
  leaveName,
  code,
}: {
  shift?: Shift;
  leaveName?: string;
  code?: string;
}) {
  if (leaveName) {
    return (
      <span className="text-[10px] font-semibold tracking-wide text-fuchsia-700">
        LV
      </span>
    );
  }
  if (shift?.is_off) {
    return (
      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        OFF
      </span>
    );
  }
  if (shift) {
    return (
      <span
        data-shift-code={code}
        className="text-[11px] font-bold tracking-wide text-slate-800"
      >
        {code}
      </span>
    );
  }
  return <span className="text-slate-300">—</span>;
}

function ShiftLegend({ templates }: { templates: ShiftTemplate[] }) {
  const active = templates.filter((template) => template.is_active);
  if (active.length === 0) return null;
  return (
    <Card padded={false} className="mb-3 px-3 py-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <h2 className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
          Shift template legend
        </h2>
        <ul className="flex flex-wrap gap-1.5">
          {active.map((template) => {
            const tone = templateTone(template);
            return (
              <li
                key={template.id}
                data-template-id={template.id}
                data-shift-tone={tone.key}
                className={`flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] ring-1 ring-inset ${tone.chip}`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} aria-hidden="true" />
                <span className={`rounded px-1 py-0.5 font-semibold ${tone.bg}`}>
                  {template.code?.trim().toUpperCase() || initialsFor(template.name)}
                </span>
                <span className="font-medium">{template.name}</span>
                <span className="tabular-nums opacity-80">
                  {template.start_time.slice(0, 5)}–{template.end_time.slice(0, 5)}
                </span>
                {template.is_default ? <span className="font-semibold">Default</span> : null}
              </li>
            );
          })}
        </ul>
      </div>
    </Card>
  );
}

function ShiftEditModal({
  employee,
  date,
  templates,
  currentShift,
  onClose,
  onSaved,
}: {
  employee?: {
    id: string;
    display_name: string;
    employee_code?: string;
  };
  date: string;
  templates: ShiftTemplate[];
  currentShift?: Shift;
  onClose: () => void;
  onSaved: () => void;
}) {
  const activeTemplates = templates.filter((template) => template.is_active);
  const initialChoice = currentShift
    ? currentShift.is_off
      ? "__off__"
      : currentShift.template ?? "__none__"
    : "__none__";
  const [choice, setChoice] = useState(initialChoice);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async (next: string) => {
      if (next === "__none__") {
        if (currentShift) await api.deleteShift(currentShift.id);
        return;
      }
      const body = shiftBodyFor(next, activeTemplates, employee, date);
      if (currentShift) await api.updateShift(currentShift.id, body);
      else await api.createShift(body);
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (caught) => setError(shiftEditErrorMessage(caught)),
  });

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!choice) {
      setError("Choose a shift, Weekly Off, or Not Assigned.");
      return;
    }
    if (!employee) {
      setError("The employee could not be found. Refresh the roster and try again.");
      return;
    }
    if (currentShift && initialChoice === choice) {
      onClose();
      return;
    }
    save.mutate(choice);
  }

  const currentTemplate = activeTemplates.find(
    (template) => template.id === currentShift?.template,
  );

  return (
    <Modal title="Edit shift" onClose={onClose} wide>
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm text-slate-700">
          <span className="font-medium text-slate-900">
            {employee?.employee_code ? `${employee.employee_code} · ` : ""}
            {employee?.display_name ?? "Employee"}
          </span>{" "}
          · {formatDay(date)}
        </p>

        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Shift templates
          </h3>
          {activeTemplates.length === 0 ? (
            <p className="text-sm text-slate-500">
              No active shift templates. Add one under Shift templates.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {activeTemplates.map((template) => {
                const selected = choice === template.id;
                const tone = templateTone(template);
                return (
                  <button
                    key={template.id}
                    type="button"
                    aria-pressed={selected}
                    data-template-id={template.id}
                    data-shift-tone={tone.key}
                    onClick={() => setChoice(template.id)}
                    className={`rounded-md px-3 py-2 text-left text-xs ring-1 ring-inset transition ${tone.chip} ${
                      selected ? "ring-2 ring-slate-900 ring-offset-1" : ""
                    }`}
                  >
                    <span className="block font-semibold">
                      {template.code ? `${template.code} · ` : ""}
                      {template.name}
                    </span>
                    <span className="block opacity-80">
                      {template.start_time.slice(0, 5)}–{template.end_time.slice(0, 5)}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Exceptions
          </h3>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              aria-pressed={choice === "__off__"}
              onClick={() => setChoice("__off__")}
              className={`rounded-md px-3 py-2 text-xs font-semibold ring-1 ring-inset ${OFF_TONE.chip} ${
                choice === "__off__" ? "ring-2 ring-slate-900 ring-offset-1" : ""
              }`}
            >
              Weekly Off (OFF)
            </button>
            <button
              type="button"
              aria-pressed={choice === "__none__"}
              onClick={() => setChoice("__none__")}
              className={`rounded-md px-3 py-2 text-xs font-semibold ring-1 ring-inset ${NEUTRAL_TONE.chip} ${
                choice === "__none__" ? "ring-2 ring-slate-900 ring-offset-1" : ""
              }`}
            >
              Not Assigned (—)
            </button>
          </div>
        </div>

        <p className="text-xs text-slate-500">
          Currently assigned:{" "}
          <span className="font-medium text-slate-700">
            {currentShift?.is_off
              ? "Weekly Off"
              : currentTemplate
                ? `${currentTemplate.name} ${currentTemplate.start_time.slice(0, 5)}–${currentTemplate.end_time.slice(0, 5)}`
                : currentShift
                  ? `${currentShift.template_name || "Shift"} ${currentShift.start_time.slice(0, 5)}–${currentShift.end_time.slice(0, 5)}`
                  : "Not Assigned"}
          </span>
        </p>

        {error ? (
          <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </p>
        ) : null}

        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={save.isPending || !employee}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function shiftBodyFor(
  choice: string,
  templates: ShiftTemplate[],
  employee: { id: string } | undefined,
  date: string,
) {
  if (!employee) throw new Error("The employee could not be found.");
  if (choice === "__off__") {
    return {
      employee: employee.id,
      shift_date: date,
      start_time: "00:00",
      end_time: "00:00",
      is_off: true,
      template: null,
    };
  }
  const template = templates.find((candidate) => candidate.id === choice);
  if (!template) throw new Error("Choose a shift template.");
  return {
    employee: employee.id,
    shift_date: date,
    start_time: template.start_time,
    end_time: template.end_time,
    break_minutes: template.break_minutes,
    template: template.id,
    is_off: false,
  };
}

function shiftEditErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const fieldMessages = Object.entries(error.fields).flatMap(
      ([field, messages]) =>
        messages.map((message) => `${field.replace(/_/g, " ")}: ${message}`),
    );
    return fieldMessages.length
      ? `${error.message} ${fieldMessages.join(" ")}`
      : error.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "The shift could not be saved. Please try again.";
}

export function isGeneralShift(template: ShiftTemplate): boolean {
  const code = (template.code ?? "").trim().toUpperCase();
  const name = template.name.trim().toLowerCase();
  return code === "G" || code === "GENERAL" || name === "general" || name === "general shift";
}

function AssignmentsPanel({
  employees,
  templates,
  branches,
  departments,
  defaultFrom,
  defaultTo,
  onChanged,
}: {
  employees: {
    id: string;
    display_name: string;
    employee_code?: string;
    branch?: string | null;
    department?: string | null;
    status?: string;
  }[];
  templates: ShiftTemplate[];
  branches: { id: string; name: string }[];
  departments: { id: string; name: string }[];
  defaultFrom: string;
  defaultTo: string;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [templateId, setTemplateId] = useState("");
  const [fromDate, setFromDate] = useState(defaultFrom);
  const [toDate, setToDate] = useState(defaultTo);
  const [specificDates, setSpecificDates] = useState<string[]>([]);
  const [notes, setNotes] = useState("");
  const [search, setSearch] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [genFrom, setGenFrom] = useState(defaultFrom);
  const [genTo, setGenTo] = useState(defaultTo);
  const [genTemplateId, setGenTemplateId] = useState("");
  const [genSundaysOff, setGenSundaysOff] = useState(true);
  const [genError, setGenError] = useState<string | null>(null);

  const assignments = useQuery({
    queryKey: ["roster-assignments"],
    queryFn: ({ signal }) => api.rosterAssignmentsPage({ page_size: 25 }, signal),
  });

  const activeEmployees = employees.filter((person) => {
    if (person.status === "exited") return false;
    if (branchFilter && person.branch !== branchFilter) return false;
    if (departmentFilter && person.department !== departmentFilter) return false;
    if (search.trim()) {
      const needle = search.trim().toLowerCase();
      return (
        person.display_name.toLowerCase().includes(needle) ||
        (person.employee_code ?? "").toLowerCase().includes(needle)
      );
    }
    return true;
  });

  const candidateDates =
    fromDate && toDate && fromDate <= toDate
      ? Array.from({ length: daysBetween(fromDate, toDate) + 1 }, (_, i) =>
          addDays(fromDate, i),
        )
      : [];

  // The company default is a form convenience, never an assignment: it only
  // chooses the starting value of the template dropdown.
  const defaultTemplate = templates.find(
    (template) => template.is_default && template.is_active,
  );
  const generalTemplate =
    templates.find((template) => template.is_active && isGeneralShift(template)) ??
    defaultTemplate ??
    templates.find((template) => template.is_active);
  const selectedTemplate = templates.find((template) => template.id === templateId);
  const selectedGenTemplate = templates.find((template) => template.id === genTemplateId);

  function openGenerate() {
    setGenerateOpen(true);
    setGenFrom(defaultFrom);
    setGenTo(defaultTo);
    setGenTemplateId(generalTemplate?.id ?? "");
    setGenSundaysOff(true);
    setGenError(null);
  }

  function resetForm() {
    setEditingId(null);
    setSelectedEmployees([]);
    setTemplateId(defaultTemplate?.id ?? "");
    setFromDate(defaultFrom);
    setToDate(defaultTo);
    setSpecificDates([]);
    setNotes("");
    setSearch("");
    setBranchFilter("");
    setDepartmentFilter("");
    setError(null);
  }

  function startEditing(assignment: RosterAssignment) {
    setEditingId(assignment.id);
    setSelectedEmployees(assignment.employee_ids ?? []);
    setTemplateId(assignment.template ?? "");
    setFromDate(assignment.from_date);
    setToDate(assignment.to_date);
    setSpecificDates(assignment.specific_dates ?? []);
    setNotes(assignment.notes ?? "");
    setError(null);
    setNotice(null);
    setOpen(true);
  }

  const createAssignment = useMutation({
    mutationFn: (body: Parameters<typeof api.createRosterAssignment>[0]) =>
      api.createRosterAssignment(body),
    onSuccess: (result) => {
      const parts = [
        `${result.created} shift${result.created === 1 ? "" : "s"} assigned.`,
      ];
      if (result.skipped.length) {
        parts.push(`${result.skipped.length} day(s) skipped.`);
      }
      if (result.conflicts.length) {
        parts.push(
          `${result.conflicts.length} overlap warning${
            result.conflicts.length === 1 ? "" : "s"
          } flagged for review.`,
        );
      }
      setNotice(parts.join(" "));
      setError(null);
      setOpen(false);
      resetForm();
      onChanged();
    },
    onError: (caught) => setError(assignmentErrorMessage(caught)),
  });

  const updateAssignment = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Parameters<typeof api.createRosterAssignment>[0];
    }) => api.updateRosterAssignment(id, body),
    onSuccess: (result) => {
      setNotice(
        `${result.created} draft shift${result.created === 1 ? "" : "s"} rebuilt.`,
      );
      setError(null);
      setOpen(false);
      resetForm();
      onChanged();
    },
    onError: (caught) => setError(assignmentErrorMessage(caught)),
  });

  const removeAssignment = useMutation({
    mutationFn: (id: string) => api.deleteRosterAssignment(id),
    onSuccess: () => {
      setNotice("Assignment removed.");
      setError(null);
      onChanged();
    },
    onError: (caught) => setError(assignmentErrorMessage(caught)),
  });

  const generateRoster = useMutation({
    mutationFn: ({
      templateId,
      body,
    }: {
      templateId: string;
      body: {
        employees: string[];
        from_date: string;
        to_date: string;
        weekly_off_days?: number[];
      };
    }) => api.generateRoster(templateId, body),
    onSuccess: (result) => {
      const parts = [
        `Generated ${result.created} shift${result.created === 1 ? "" : "s"}.`,
      ];
      if (result.skipped.length) {
        parts.push(`${result.skipped.length} day(s) skipped.`);
      }
      setNotice(parts.join(" "));
      setError(null);
      setGenerateOpen(false);
      onChanged();
    },
    onError: (caught) => setGenError(assignmentErrorMessage(caught)),
  });

  function closeGenerate() {
    if (generateRoster.isPending) return;
    setGenerateOpen(false);
    setGenError(null);
  }

  function submitGenerate(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setGenError(null);
    if (!genTemplateId) {
      setGenError("Choose the working-day shift template.");
      return;
    }
    if (!genFrom || !genTo || genTo < genFrom) {
      setGenError("The end date must be on or after the start date.");
      return;
    }
    if (activeEmployees.length === 0) {
      setGenError("No active employees match the current filters.");
      return;
    }
    generateRoster.mutate({
      templateId: genTemplateId,
      body: {
        employees: activeEmployees.map((person) => person.id),
        from_date: genFrom,
        to_date: genTo,
        ...(genSundaysOff ? { weekly_off_days: [6] } : {}),
      },
    });
  }

  const busy =
    createAssignment.isPending ||
    updateAssignment.isPending ||
    removeAssignment.isPending;

  function toggleEmployee(id: string) {
    setSelectedEmployees((current) =>
      current.includes(id)
        ? current.filter((value) => value !== id)
        : [...current, id],
    );
  }

  function toggleDate(day: string) {
    setSpecificDates((current) =>
      current.includes(day)
        ? current.filter((value) => value !== day)
        : [...current, day],
    );
  }

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!templateId) {
      setError("Choose a shift template.");
      return;
    }
    if (selectedEmployees.length === 0) {
      setError("Select at least one employee.");
      return;
    }
    if (!fromDate || !toDate || toDate < fromDate) {
      setError("The end date must be on or after the start date.");
      return;
    }
    const body = {
      template: templateId,
      employees: selectedEmployees,
      from_date: fromDate,
      to_date: toDate,
      ...(specificDates.length ? { specific_dates: specificDates } : {}),
      notes,
    };
    if (editingId) updateAssignment.mutate({ id: editingId, body });
    else createAssignment.mutate(body);
  }

  const rows = assignments.data?.results ?? [];

  return (
    <>
      <div
        data-section="assignments-toolbar"
        className="mb-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2 rounded-lg border border-slate-200 bg-white px-3 py-2"
      >
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <h2 className="text-xs font-semibold text-slate-900">Assignments</h2>
          <p className="text-[11px] text-slate-500">
            Manage employee shift assignments.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Button type="button" size="sm" onClick={openGenerate}>
            <Repeat size={13} /> Generate roster
          </Button>
          {!open ? (
            <Button
              size="sm"
              onClick={() => {
                resetForm();
                setOpen(true);
              }}
            >
              <Plus size={13} /> Assign shift
            </Button>
          ) : null}
        </div>
      </div>

      {notice ? (
        <p role="status" className="mb-2 rounded-md bg-emerald-50 px-2 py-1 text-xs text-emerald-800">
          {notice}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="mb-2 rounded-md bg-rose-50 px-2 py-1 text-xs text-rose-700">
          {error}
        </p>
      ) : null}

      {open ? (
        <Modal
          title={editingId ? "Edit assignment" : "Assign shift"}
          onClose={() => {
            if (busy) return;
            setOpen(false);
            resetForm();
          }}
          wide
        >
        <form onSubmit={submit} className="space-y-4">
          <div>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Employees
              </h3>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() =>
                    setSelectedEmployees((current) => [
                      ...new Set([
                        ...current,
                        ...activeEmployees.map((person) => person.id),
                      ]),
                    ])
                  }
                >
                  Select visible
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setSelectedEmployees([])}
                >
                  Clear
                </Button>
              </div>
            </div>
            <div className="mb-2 grid gap-2 sm:grid-cols-3">
              <label className="relative block">
                <Search
                  size={14}
                className="pointer-events-none absolute left-2 top-2 text-slate-400"
                />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search employees"
                  className={`${inputClass} pl-7`}
                />
              </label>
              <select
                value={branchFilter}
                onChange={(event) => setBranchFilter(event.target.value)}
                className={inputClass}
              >
                <option value="">All branches</option>
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
              <select
                value={departmentFilter}
                onChange={(event) => setDepartmentFilter(event.target.value)}
                className={inputClass}
              >
                <option value="">All departments</option>
                {departments.map((department) => (
                  <option key={department.id} value={department.id}>
                    {department.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="max-h-44 overflow-y-auto rounded-md border border-slate-200 bg-slate-50 p-2">
              {activeEmployees.length === 0 ? (
                <p className="p-2 text-sm text-slate-500">
                  No active employees match those filters.
                </p>
              ) : (
                <div className="grid gap-1 sm:grid-cols-2">
                  {activeEmployees.map((person) => (
                    <label
                      key={person.id}
                      className="flex items-center gap-2 rounded px-2 py-1 text-sm text-slate-700 hover:bg-white"
                    >
                      <input
                        type="checkbox"
                        checked={selectedEmployees.includes(person.id)}
                        onChange={() => toggleEmployee(person.id)}
                      />
                      <span className="truncate">
                        {person.display_name}
                        {person.employee_code ? (
                          <span className="ml-1 text-xs text-slate-400">
                            {person.employee_code}
                          </span>
                        ) : null}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <p className="mt-1 text-xs text-slate-500">
              {selectedEmployees.length} employee
              {selectedEmployees.length === 1 ? "" : "s"} selected.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <label className="block text-xs font-medium text-slate-600">
              Shift template
              <select
                value={templateId}
                onChange={(event) => setTemplateId(event.target.value)}
                className={`${inputClass} mt-1`}
                required
              >
                <option value="">Choose a template</option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.code ? `${template.code} · ` : ""}
                    {template.name}
                    {template.is_default ? " (default)" : ""}
                  </option>
                ))}
              </select>
              {defaultTemplate ? (
                <span className="mt-1 block text-[11px] font-normal text-slate-500">
                  Default shift preselected.
                </span>
              ) : null}
              {selectedTemplate ? (
                <span
                  data-template-id={selectedTemplate.id}
                  data-shift-tone={templateTone(selectedTemplate).key}
                  className={`mt-1 ${templateChip(selectedTemplate)}`}
                >
                  {selectedTemplate.code ? `${selectedTemplate.code} · ` : ""}
                  {selectedTemplate.name}
                </span>
              ) : null}
            </label>
            <label className="block text-xs font-medium text-slate-600">
              Start date
              <input
                type="date"
                value={fromDate}
                onChange={(event) => {
                  setFromDate(event.target.value);
                  setSpecificDates([]);
                }}
                className={`${inputClass} mt-1`}
                required
              />
            </label>
            <label className="block text-xs font-medium text-slate-600">
              End date
              <input
                type="date"
                value={toDate}
                onChange={(event) => {
                  setToDate(event.target.value);
                  setSpecificDates([]);
                }}
                className={`${inputClass} mt-1`}
                required
              />
            </label>
          </div>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Optional specific dates
            </h3>
            <p className="mt-1 text-xs text-slate-500">
              Leave every box unchecked to use all working days in the range.
              Check dates to assign this shift only on those days.
            </p>
            <div className="mt-2 flex max-h-32 flex-wrap gap-1 overflow-y-auto rounded-md border border-slate-200 bg-slate-50 p-2">
              {candidateDates.length === 0 ? (
                <span className="p-1 text-sm text-slate-500">
                  Choose a valid date range first.
                </span>
              ) : (
                candidateDates.map((day) => (
                  <label
                    key={day}
                    className="flex items-center gap-1 rounded bg-white px-2 py-1 text-xs text-slate-600 ring-1 ring-inset ring-slate-200"
                  >
                    <input
                      type="checkbox"
                      checked={specificDates.includes(day)}
                      onChange={() => toggleDate(day)}
                    />
                    {formatDay(day)}
                  </label>
                ))
              )}
            </div>
          </div>

          <label className="block text-xs font-medium text-slate-600">
            Notes (optional)
            <input
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              maxLength={200}
              className={`${inputClass} mt-1`}
              placeholder="e.g. Opening week cover"
            />
          </label>

          <div className="flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              onClick={() => {
                setOpen(false);
                resetForm();
              }}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={busy}>
              {editingId ? "Save assignment changes" : "Save assignment"}
            </Button>
          </div>
        </form>
        </Modal>
      ) : null}

      {assignments.isPending ? (
        <Skeleton className="mb-3 h-12 w-full" />
      ) : rows.length > 0 ? (
        <div className="mb-3 overflow-hidden rounded-lg border border-slate-200 bg-white px-3 py-1.5">
          <ul className="max-h-64 divide-y divide-slate-100 overflow-y-auto">
            {rows.map((assignment) => {
              const locked = assignment.published_count > 0;
              const assignmentTemplate = templates.find(
                (template) => template.id === assignment.template,
              );
              const assignmentTone = assignmentTemplate
                ? templateTone(assignmentTemplate)
                : shiftToneFor(assignment.template_name);
              return (
                <li key={assignment.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
                  <div className="min-w-0">
                    <p className="flex flex-wrap items-center gap-1.5 text-xs font-medium text-slate-900">
                      <span
                        data-template-id={assignment.template ?? undefined}
                        data-shift-tone={assignmentTone.key}
                        className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ${assignmentTone.chip}`}
                      >
                        <span className={`h-1.5 w-1.5 rounded-full ${assignmentTone.dot}`} aria-hidden="true" />
                        {assignment.template_name || "Assignment"}
                      </span>
                      {assignment.draft_count ? (
                        <Badge tone="amber">{assignment.draft_count} draft</Badge>
                      ) : null}
                      {assignment.published_count ? (
                        <Badge tone="emerald">
                          {assignment.published_count} published
                        </Badge>
                      ) : null}
                    </p>
                    <p className="text-[11px] text-slate-500">
                      {formatDay(assignment.from_date)} - {formatDay(assignment.to_date)}{" "}
                      &middot; {assignment.employee_count} employee
                      {assignment.employee_count === 1 ? "" : "s"} &middot;{" "}
                      {assignment.shift_count} shift
                      {assignment.shift_count === 1 ? "" : "s"}
                      {assignment.specific_dates.length
                        ? ` on ${assignment.specific_dates.length} selected date${
                            assignment.specific_dates.length === 1 ? "" : "s"
                          }`
                        : ""}
                      {assignment.notes ? ` \u00b7 ${assignment.notes}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-0.5">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => startEditing(assignment)}
                      disabled={locked}
                      title={
                        locked
                          ? "Unpublish this assignment before editing it."
                          : "Edit draft assignment"
                      }
                    >
                      <Pencil size={13} /> Edit
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => removeAssignment.mutate(assignment.id)}
                      disabled={locked || removeAssignment.isPending}
                      title={
                        locked
                          ? "Unpublish this assignment before removing it."
                          : "Remove draft assignment"
                      }
                    >
                      <Trash2 size={13} /> Remove
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
      {generateOpen ? (
        <Modal title="Generate roster" onClose={closeGenerate} wide>
          <form onSubmit={submitGenerate} className="space-y-4">
            <p className="text-sm text-slate-600">
              Generate a shift for each working day in the selected range. Dates
              outside this range stay Not Assigned.
            </p>

            <div className="grid gap-3 sm:grid-cols-3">
              <label className="block text-xs font-medium text-slate-600">
                Working-day shift
                <select
                  value={genTemplateId}
                  onChange={(event) => setGenTemplateId(event.target.value)}
                  className={`${inputClass} mt-1`}
                  required
                >
                  <option value="">Choose a template</option>
                  {templates.filter((template) => template.is_active).map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.code ? `${template.code} · ` : ""}
                      {template.name}
                      {isGeneralShift(template) ? " (General Shift)" : ""}
                    </option>
                  ))}
                </select>
                {selectedGenTemplate ? (
                  <span
                    data-template-id={selectedGenTemplate.id}
                    data-shift-tone={templateTone(selectedGenTemplate).key}
                    className={`mt-1 ${templateChip(selectedGenTemplate)}`}
                  >
                    {selectedGenTemplate.code ? `${selectedGenTemplate.code} · ` : ""}
                    {selectedGenTemplate.name}
                  </span>
                ) : null}
              </label>
              <label className="block text-xs font-medium text-slate-600">
                Start date
                <input
                  type="date"
                  value={genFrom}
                  onChange={(event) => setGenFrom(event.target.value)}
                  className={`${inputClass} mt-1`}
                  required
                />
              </label>
              <label className="block text-xs font-medium text-slate-600">
                End date
                <input
                  type="date"
                  value={genTo}
                  onChange={(event) => setGenTo(event.target.value)}
                  className={`${inputClass} mt-1`}
                  required
                />
              </label>
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={genSundaysOff}
                onChange={(event) => setGenSundaysOff(event.target.checked)}
              />
              Sundays are Weekly Off (OFF)
            </label>

            <p className="text-xs text-slate-500">
              {activeEmployees.length} active employee
              {activeEmployees.length === 1 ? "" : "s"} match the current
              filters.
            </p>

            {generalTemplate ? (
              <p className="text-xs text-slate-500">
                Default working-day shift:{" "}
                <span className="font-medium text-slate-700">
                  {generalTemplate.name}
                </span>
                .
              </p>
            ) : null}

            {genError ? (
              <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {genError}
              </p>
            ) : null}

            <div className="flex flex-wrap justify-end gap-2">
              <Button type="button" onClick={closeGenerate} disabled={generateRoster.isPending}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={generateRoster.isPending || activeEmployees.length === 0}
              >
                {generateRoster.isPending ? "Generating…" : "Generate roster"}
              </Button>
            </div>
          </form>
        </Modal>
      ) : null}
    </>
  );
}

function DefaultShiftPanel({
  templates,
  canSetDefault,
  onChanged,
}: {
  templates: ShiftTemplate[];
  canSetDefault: boolean;
  onChanged: () => void;
}) {
  const setDefault = useMutation({
    mutationFn: ({ id, isDefault }: { id: string; isDefault: boolean }) =>
      isDefault
        ? api.setDefaultShiftTemplate(id)
        : api.unsetDefaultShiftTemplate(id),
    onSuccess: onChanged,
  });
  const currentDefault = templates.find((template) => template.is_default);
  const activeTemplates = templates.filter((template) => template.is_active);

  return (
    <Card className="mb-4">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">Default shift</h2>
          <p className="mt-1 text-xs text-slate-500">
            New assignments preselect this template. Changes only affect future assignments.
          </p>
        </div>
        {currentDefault ? (
          <span
            data-template-id={currentDefault.id}
            data-shift-tone={templateTone(currentDefault).key}
            className={templateChip(currentDefault)}
          >
            <span className={`h-2 w-2 rounded-full ${templateTone(currentDefault).dot}`} aria-hidden="true" />
            {currentDefault.code ? `${currentDefault.code} · ` : ""}
            {currentDefault.name}
          </span>
        ) : (
          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
            No default shift
          </span>
        )}
      </div>

      {!canSetDefault ? (
        <p className="mb-3 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600 ring-1 ring-inset ring-slate-200">
          Only the account owner or an administrator can change the company default shift.
        </p>
      ) : null}

      {activeTemplates.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500">
          Create an active shift template before choosing a default shift.
        </p>
      ) : (
        <ul className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {activeTemplates.map((template) => {
            const selected = template.is_default;
            const tone = templateTone(template);
            return (
              <li key={template.id}>
                <button
                  type="button"
                  aria-pressed={selected}
                  disabled={!canSetDefault || setDefault.isPending}
                  data-template-id={template.id}
                  data-shift-tone={tone.key}
                  onClick={() => setDefault.mutate({ id: template.id, isDefault: true })}
                  className={`w-full rounded-md px-3 py-2 text-left text-xs ring-1 ring-inset transition ${tone.chip} ${
                    selected ? "ring-2 ring-slate-900 ring-offset-1" : ""
                  } disabled:cursor-not-allowed disabled:opacity-60`}
                >
                  <span className="block font-semibold">
                    {template.code ? `${template.code} · ` : ""}
                    {template.name}
                  </span>
                  <span className="block opacity-80">
                    {template.start_time.slice(0, 5)}–{template.end_time.slice(0, 5)}
                    {template.spans_midnight ? " (overnight)" : ""}
                  </span>
                  {selected ? <span className="mt-1 block font-semibold">Current default</span> : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {currentDefault && canSetDefault ? (
        <div className="mt-3 flex justify-end">
          <Button
            variant="ghost"
            disabled={setDefault.isPending}
            onClick={() => setDefault.mutate({ id: currentDefault.id, isDefault: false })}
          >
            Clear default shift
          </Button>
        </div>
      ) : null}

      {setDefault.error instanceof ApiError ? (
        <p role="alert" className="mt-2 text-sm text-rose-600">
          {setDefault.error.message}
        </p>
      ) : null}
    </Card>
  );
}

function TemplatesPanel({
  templates,
  employees,
  onChanged,
}: {
  templates: ShiftTemplate[];
  employees: { id: string; display_name: string; status?: string }[];
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [genFrom, setGenFrom] = useState("");
  const [genTo, setGenTo] = useState("");
  const [genResult, setGenResult] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (body: {
      name: string;
      code?: string;
      description?: string;
      is_active?: boolean;
      start_time: string;
      end_time: string;
      break_minutes?: number;
      grace_minutes?: number;
      days_of_week?: number[];
    }) => api.createShiftTemplate(body),
    onSuccess: onChanged,
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<ShiftTemplateWrite> }) =>
      api.updateShiftTemplate(id, body),
    onSuccess: onChanged,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.deleteShiftTemplate(id),
    onSuccess: () => {
      setDeleteId(null);
      onChanged();
    },
  });
  const generate = useMutation({
    mutationFn: ({
      templateId,
      body,
    }: {
      templateId: string;
      body: { employees: string[]; from_date: string; to_date: string };
    }) => api.generateRoster(templateId, body),
    onSuccess: (result) => {
      setGenResult(`Created ${result.created}, skipped ${result.skipped.length}.`);
      onChanged();
    },
  });

  const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  function collectDays(form: HTMLFormElement): number[] {
    const data = new FormData(form);
    return data
      .getAll("days")
      .map((value) => Number(value))
      .filter((value) => !Number.isNaN(value));
  }

  return (
    <Card padded={false} className="mb-3 px-3 py-2">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-xs font-semibold text-slate-900">Shift templates</h2>
        {!open ? (
          <Button size="sm" onClick={() => setOpen(true)}>
            <Plus size={13} /> New template
          </Button>
        ) : null}
      </div>
      {templates.length === 0 ? (
        <p className="text-xs text-slate-500">
          No shift templates yet. Create reusable shifts such as Morning, General, Evening
          or Closing, with their break.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {templates.map((template) => (
            <li key={template.id} className="py-1.5">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-1.5 text-xs font-medium text-slate-900">
                    <span
                      data-template-id={template.id}
                      data-shift-tone={templateTone(template).key}
                      className={templateChip(template)}
                    >
                      <span className={`h-2 w-2 rounded-full ${templateTone(template).dot}`} aria-hidden="true" />
                      {template.code ? `${template.code} · ` : ""}
                      {template.name}
                    </span>
                    {template.is_default ? (
                      <Badge tone="emerald">Default</Badge>
                    ) : null}
                    {template.is_active ? (
                      <Badge tone="slate">Active</Badge>
                    ) : (
                      <Badge tone="amber">Inactive</Badge>
                    )}
                  </p>
                  <p className="text-[11px] text-slate-500">
                    {template.start_time.slice(0, 5)}–{template.end_time.slice(0, 5)}
                    {template.spans_midnight ? " (overnight)" : ""} ·{" "}
                    {formatMinutes(template.duration_minutes)}
                    {template.break_minutes ? ` · ${template.break_minutes}m break` : ""}
                    {template.grace_minutes ? ` · ${template.grace_minutes}m grace` : ""}
                    {template.days_of_week && Array.isArray(template.days_of_week) && template.days_of_week.length
                      ? ` · days ${template.days_of_week.join(",")}`
                      : ""}
                    {template.description ? ` · ${template.description}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      setGeneratingId((current) => (current === template.id ? null : template.id))
                    }
                  >
                    Generate
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      setEditingId((current) => (current === template.id ? null : template.id))
                    }
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    title="Delete"
                    disabled={remove.isPending}
                    onClick={() => setDeleteId(template.id)}
                  >
                    <Trash2 size={13} />
                  </Button>
                </div>
              </div>

              {editingId === template.id ? (
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    const form = event.currentTarget;
                    const data = new FormData(form);
                    update.mutate({
                      id: template.id,
                      body: {
                        name: String(data.get("name") ?? "").trim(),
                        code: String(data.get("code") ?? "").trim(),
                        description: String(data.get("description") ?? "").trim(),
                        is_active: data.get("is_active") === "on",
                        start_time: String(data.get("start") ?? template.start_time),
                        end_time: String(data.get("end") ?? template.end_time),
                        break_minutes: Number(data.get("brk") ?? template.break_minutes) || 0,
                        grace_minutes: Number(data.get("grace") ?? template.grace_minutes) || 0,
                        days_of_week: collectDays(form),
                      },
                    });
                    setEditingId(null);
                  }}
                  className="mt-3 grid gap-3 border-t border-slate-100 pt-3 sm:grid-cols-4"
                >
                  <input name="name" required defaultValue={template.name} placeholder="Name" className={inputClass} />
                  <input name="code" defaultValue={template.code} placeholder="Code" className={inputClass} />
                  <input name="description" defaultValue={template.description} placeholder="Description" className={`${inputClass} sm:col-span-2`} />
                  <input name="start" type="time" defaultValue={template.start_time.slice(0, 5)} className={inputClass} />
                  <input name="end" type="time" defaultValue={template.end_time.slice(0, 5)} className={inputClass} />
                  <input name="brk" type="number" min={0} defaultValue={template.break_minutes} placeholder="Break (min)" className={inputClass} />
                  <input name="grace" type="number" min={0} defaultValue={template.grace_minutes} placeholder="Grace (min)" className={inputClass} />
                  <label className="flex items-center gap-2 text-xs font-medium text-slate-600">
                    <input type="checkbox" name="is_active" defaultChecked={template.is_active} />
                    Active template
                  </label>
                  <div className="sm:col-span-4 flex flex-wrap gap-2">
                    {dayNames.map((label, index) => (
                      <label key={label} className="flex items-center gap-1 text-xs text-slate-600">
                        <input
                          type="checkbox"
                          name="days"
                          value={index}
                          defaultChecked={Array.isArray(template.days_of_week) && template.days_of_week.includes(index)}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                  {update.error instanceof ApiError ? (
                    <p role="alert" className="text-sm text-rose-600 sm:col-span-4">{update.error.message}</p>
                  ) : null}
                  <div className="flex justify-end gap-2 sm:col-span-4">
                    <Button onClick={() => setEditingId(null)}>Cancel</Button>
                    <Button type="submit" variant="primary" disabled={update.isPending}>
                      Save
                    </Button>
                  </div>
                </form>
              ) : null}

              {generatingId === template.id ? (
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    generate.mutate({
                      templateId: template.id,
                      body: {
                        employees: employees.map((employee) => employee.id),
                        from_date: genFrom,
                        to_date: genTo,
                      },
                    });
                  }}
                  className="mt-3 grid gap-3 border-t border-slate-100 pt-3 sm:grid-cols-3"
                >
                  <input
                    type="date"
                    value={genFrom}
                    onChange={(event) => setGenFrom(event.target.value)}
                    className={inputClass}
                    placeholder="From"
                  />
                  <input
                    type="date"
                    value={genTo}
                    onChange={(event) => setGenTo(event.target.value)}
                    className={inputClass}
                    placeholder="To"
                  />
                  <Button type="submit" variant="primary" disabled={generate.isPending}>
                    Generate roster
                  </Button>
                  {genResult ? <p className="text-sm text-slate-600 sm:col-span-3">{genResult}</p> : null}
                  {generate.error instanceof ApiError ? (
                    <p role="alert" className="text-sm text-rose-600 sm:col-span-3">{generate.error.message}</p>
                  ) : null}
                </form>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {remove.error instanceof ApiError ? (
        <p role="alert" className="mt-2 text-sm text-rose-600">
          {remove.error.message}
        </p>
      ) : null}
      {deleteId ? (
        <Modal
          title="Delete shift template"
          onClose={() => {
            if (remove.isPending) return;
            setDeleteId(null);
          }}
        >
          <p className="text-sm text-slate-600">
            Delete{" "}
            <strong>{templates.find((template) => template.id === deleteId)?.name}</strong>?
            Templates that are already used by assignments or shifts cannot be removed.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button disabled={remove.isPending} onClick={() => setDeleteId(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={remove.isPending}
              onClick={() => remove.mutate(deleteId)}
            >
              {remove.isPending ? "Deleting…" : "Delete template"}
            </Button>
          </div>
        </Modal>
      ) : null}
      {open ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const form = event.currentTarget;
            const data = new FormData(form);
            create.mutate({
              name: String(data.get("name") ?? "").trim(),
              code: String(data.get("code") ?? "").trim(),
              description: String(data.get("description") ?? "").trim(),
              is_active: data.get("is_active") === "on",
              start_time: String(data.get("start") ?? "09:00"),
              end_time: String(data.get("end") ?? "18:00"),
              break_minutes: Number(data.get("brk") ?? 0) || 0,
              grace_minutes: Number(data.get("grace") ?? 0) || 0,
              days_of_week: collectDays(form),
            });
            form.reset();
            setOpen(false);
          }}
          className="mt-3 grid gap-3 border-t border-slate-100 pt-3 sm:grid-cols-4"
        >
          <input name="name" required placeholder="Name (e.g. General)" className={inputClass} />
          <input name="code" placeholder="Code" className={inputClass} />
          <input name="description" placeholder="Description" className={`${inputClass} sm:col-span-2`} />
          <input name="start" type="time" defaultValue="10:00" className={inputClass} />
          <input name="end" type="time" defaultValue="19:00" className={inputClass} />
          <input name="brk" type="number" min={0} defaultValue={60} placeholder="Break (min)" className={inputClass} />
          <input name="grace" type="number" min={0} defaultValue={0} placeholder="Grace (min)" className={inputClass} />
          <label className="flex items-center gap-2 text-xs font-medium text-slate-600">
            <input type="checkbox" name="is_active" defaultChecked />
            Active template
          </label>
          <div className="sm:col-span-4 flex flex-wrap gap-2">
            {dayNames.map((label, index) => (
              <label key={label} className="flex items-center gap-1 text-xs text-slate-600">
                <input type="checkbox" name="days" value={index} />
                {label}
              </label>
            ))}
          </div>
          {create.error instanceof ApiError ? (
            <p role="alert" className="text-sm text-rose-600 sm:col-span-4">{create.error.message}</p>
          ) : null}
          <div className="flex justify-end gap-2 sm:col-span-4">
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={create.isPending}>
              Save template
            </Button>
          </div>
        </form>
      ) : null}
    </Card>
  );
}

function cellClass(
  off: boolean,
  onLeave: boolean,
  clickable: boolean,
  conflict: boolean = false,
  templateKey?: string | null,
): string {
  const base =
    "flex h-8 w-full items-center justify-center rounded px-1 text-center text-[11px] leading-none ring-1 ring-inset transition";
  if (onLeave) {
    return `${base} ${LEAVE_TONE.bg} ${LEAVE_TONE.text} ${clickable ? LEAVE_TONE.hover : ""} ${LEAVE_TONE.ring} ${clickable ? "cursor-pointer" : "cursor-default"}`;
  }
  if (off) {
    return `${base} ${OFF_TONE.bg} ${OFF_TONE.text} ${clickable ? OFF_TONE.hover : ""} ${OFF_TONE.ring} ${clickable ? "cursor-pointer" : "cursor-default"}`;
  }
  const tone = shiftToneFor(templateKey);
  return `${base} ${tone.bg} ${tone.text} ${clickable ? tone.hover : ""} ${
    conflict ? "ring-2 ring-amber-400" : tone.ring
  } ${clickable ? "cursor-pointer" : "cursor-default"}`;
}

const inputClass =
  "w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900";
const selectClass =
  "rounded-md border-0 px-2 py-1 text-xs ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900";

function assignmentErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const fieldMessages = Object.entries(error.fields).flatMap(
      ([field, messages]) =>
        messages.map((message) => `${field.replace(/_/g, " ")}: ${message}`),
    );
    return fieldMessages.length
      ? `${error.message} ${fieldMessages.join(" ")}`
      : error.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "The assignment could not be saved. Please try again.";
}

function isoDate(value: Date): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(value);
  const map: Record<string, string> = {};
  for (const part of parts) map[part.type] = part.value;
  return `${map.year}-${map.month}-${map.day}`;
}

function addDays(iso: string, days: number): string {
  const value = new Date(`${iso}T00:00:00`);
  value.setDate(value.getDate() + days);
  return isoDate(value);
}

function startOfWeek(iso: string): string {
  const value = new Date(`${iso}T00:00:00`);
  const offset = (value.getDay() + 6) % 7;
  value.setDate(value.getDate() - offset);
  return isoDate(value);
}

export function startOfMonth(iso: string): string {
  const value = new Date(`${iso}T00:00:00`);
  value.setDate(1);
  return isoDate(value);
}

export function endOfMonth(iso: string): string {
  const value = new Date(`${iso}T00:00:00`);
  value.setMonth(value.getMonth() + 1, 0);
  return isoDate(value);
}

/** Every calendar day in the month containing ``iso``, leap years included. */
export function monthDays(iso: string): string[] {
  const start = startOfMonth(iso);
  const end = endOfMonth(iso);
  return Array.from(
    { length: daysBetween(start, end) + 1 },
    (_, index) => addDays(start, index),
  );
}

export function addMonths(iso: string, months: number): string {
  const value = new Date(`${iso}T00:00:00`);
  value.setMonth(value.getMonth() + months, 1);
  return isoDate(value);
}

function daysBetween(from: string, to: string): number {
  const start = new Date(`${from}T00:00:00`).getTime();
  const end = new Date(`${to}T00:00:00`).getTime();
  return Math.max(0, Math.round((end - start) / 86_400_000));
}

function weekdayShort(iso: string): string {
  const value = new Date(`${iso}T00:00:00`);
  return DAY_NAMES[(value.getDay() + 6) % 7] ?? "";
}

function dayNumber(iso: string): string {
  return String(Number(iso.slice(8, 10)));
}

function formatDay(iso: string): string {
  const value = new Date(`${iso}T00:00:00`);
  return value.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function formatMonthYear(iso: string): string {
  const value = new Date(`${iso}T00:00:00`);
  return value.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function formatRangeDay(iso: string): string {
  const value = new Date(`${iso}T00:00:00`);
  return value.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

export function formatWeekRange(from: string, to: string): string {
  return `${formatRangeDay(from)} - ${formatRangeDay(to)}`;
}

/**
 * Publish every draft shift in a week and fail as one unit.
 *
 * A partial failure still refreshes the board from the caller, so retrying
 * sends only the drafts the API did not accept. The API is called once per
 * draft; the caller's pending state is what prevents a duplicate submission.
 */
export async function publishDraftShifts(
  drafts: Pick<Shift, "id">[],
  publish: (id: string) => Promise<unknown>,
): Promise<void> {
  if (drafts.length === 0) {
    throw new Error("There are no draft shifts to publish.");
  }
  const results = await Promise.allSettled(
    drafts.map((shift) => publish(shift.id)),
  );
  const failures = results.filter(
    (result): result is PromiseRejectedResult => result.status === "rejected",
  );
  if (failures.length === 0) return;

  if (
    failures.length === 1 &&
    drafts.length === 1 &&
    failures[0]?.reason instanceof ApiError
  ) {
    throw failures[0].reason;
  }
  const prefix =
    drafts.length > 1
      ? `${failures.length} of ${drafts.length} draft shifts could not be published. `
      : "";
  throw new Error(`${prefix}${errorMessage(failures[0]?.reason)}`);
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const fieldMessages = Object.entries(error.fields).flatMap(
      ([field, messages]) =>
        messages.map((message) => `${field.replace(/_/g, " ")}: ${message}`),
    );
    return fieldMessages.length
      ? `${error.message} ${fieldMessages.join(" ")}`
      : error.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Roster could not be published. Please try again.";
}
